import {
  BookingProductType,
  BookingState,
  BookingTariff,
  isBookingProductTypePackage,
  isBookingProductTypeTariff,
  SetProductBookingAmanoParkingReservation
} from '@products/models/booking.model';
import { SetAmanoParkingReservationDate } from '@products/models/parking.model';
import {
  findBookedPackageTariff,
  findBookedTariff,
  setTariffPriceToAmanoParkingReservationTotalPrice
} from '@store/products/booking/product/product-booking.reducer';
import { cloneDeep } from 'lodash';

/**
 * Set Amano parking reservation date in booking
 * @description Set Amano parking reservation date in booking
 * @param state
 * @param props
 * @returns State
 */
export const setAmanoParkingReservationDateInBooking = (
  state: BookingState,
  props: { amanoParkingReservationDate: SetAmanoParkingReservationDate }
) => {
  const setAmanoParkingReservationDate: SetAmanoParkingReservationDate = cloneDeep(props.amanoParkingReservationDate);

  if (setAmanoParkingReservationDate) {
    const cloneStateProductsList: BookingProductType[] = cloneDeep(state.list);
    const {
      productType,
      ticketTypeId,
      ticketPersonId,
      voucherCode,
      packageNumber,
      packageIndex,
      bookingReservationIndex,
      since,
      until
    } = setAmanoParkingReservationDate;

    const setAmanoParkingReservation = (bookedTariff: BookingTariff, index: number, since: Date, until: Date) => {
      const bookedTariffAmanoParkingReservation = bookedTariff.amanoParkingReservations[index];
      if (bookedTariffAmanoParkingReservation) {
        bookedTariffAmanoParkingReservation.since = since;
        bookedTariffAmanoParkingReservation.until = until;
        bookedTariffAmanoParkingReservation.isValid = null;
      }
    };

    cloneStateProductsList.forEach((bookedProductType) => {
      if (bookedProductType.productType === productType) {
        if (isBookingProductTypeTariff(bookedProductType)) {
          const bookedTariff = findBookedTariff(bookedProductType, ticketTypeId, ticketPersonId, voucherCode);

          if (bookedTariff) {
            setAmanoParkingReservation(bookedTariff, bookingReservationIndex, since, until);

            return;
          }
        } else if (isBookingProductTypePackage(bookedProductType)) {
          if (bookedProductType.packageNumber === packageNumber && bookedProductType.packageIndex === packageIndex) {
            bookedProductType.productTypes.forEach((bookedPackageProductType) => {
              if (isBookingProductTypeTariff(bookedPackageProductType)) {
                const bookedPackageTariff = findBookedPackageTariff(
                  bookedPackageProductType,
                  ticketTypeId,
                  ticketPersonId,
                  voucherCode,
                  packageNumber,
                  packageIndex
                );

                if (bookedPackageTariff) {
                  setAmanoParkingReservation(bookedPackageTariff, bookingReservationIndex, since, until);

                  return;
                }
              }
            });
          }
        }
      }
    });

    return {
      ...state,
      list: cloneStateProductsList
    };
  }

  return state;
};

/**
 * Set validated Amano parking reservation date in booking
 * @description Set validated Amano parking reservation date in booking
 * @param state
 * @param props
 * @returns State
 */
export const setValidatedAmanoParkingReservationDateInBooking = (
  state: BookingState,
  props: SetProductBookingAmanoParkingReservation
) => {
  const setProductBookingAmanoParkingReservation: SetProductBookingAmanoParkingReservation = cloneDeep(props);

  if (setProductBookingAmanoParkingReservation) {
    const cloneStateProductsList: BookingProductType[] = cloneDeep(state.list);
    const {
      productType,
      ticketTypeId,
      ticketPersonId,
      voucherCode,
      packageNumber,
      packageIndex,
      bookingReservationIndex,
      isValid,
      price
    } = setProductBookingAmanoParkingReservation;

    const setValidatedAmanoParkingReservationInBooking = (
      bookedTariff: BookingTariff,
      isValid: boolean,
      price: number
    ) => {
      const bookedTariffAmanoParkingReservation = bookedTariff.amanoParkingReservations[bookingReservationIndex];
      if (bookedTariffAmanoParkingReservation) {
        bookedTariffAmanoParkingReservation.since = isValid ? bookedTariffAmanoParkingReservation.since : null;
        bookedTariffAmanoParkingReservation.until = isValid ? bookedTariffAmanoParkingReservation.until : null;
        bookedTariffAmanoParkingReservation.isValid = isValid;
        bookedTariffAmanoParkingReservation.price = price;
      }
    };

    cloneStateProductsList.forEach((bookedProductType) => {
      if (bookedProductType.productType === productType) {
        if (isBookingProductTypeTariff(bookedProductType)) {
          const bookedTariff = findBookedTariff(bookedProductType, ticketTypeId, ticketPersonId, voucherCode);

          if (bookedTariff) {
            setValidatedAmanoParkingReservationInBooking(bookedTariff, isValid, price);

            bookedTariff.price = setTariffPriceToAmanoParkingReservationTotalPrice(bookedTariff);

            return;
          }
        } else if (isBookingProductTypePackage(bookedProductType)) {
          if (bookedProductType.packageNumber === packageNumber && bookedProductType.packageIndex === packageIndex) {
            bookedProductType.productTypes.forEach((bookedPackageProductType) => {
              if (isBookingProductTypeTariff(bookedPackageProductType)) {
                const bookedPackageTariff = findBookedPackageTariff(
                  bookedPackageProductType,
                  ticketTypeId,
                  ticketPersonId,
                  voucherCode,
                  packageNumber,
                  packageIndex
                );

                if (bookedPackageTariff) {
                  setValidatedAmanoParkingReservationInBooking(bookedPackageTariff, isValid, price);

                  bookedPackageTariff.price = setTariffPriceToAmanoParkingReservationTotalPrice(bookedPackageTariff);

                  return;
                }
              }
            });
          }
        }
      }
    });

    return {
      ...state,
      list: cloneStateProductsList
    };
  }

  return state;
};
